import { Tables, Address, isAddressAllOf } from "./AddressTypes";

let tables: Tables;
(async() => {
	tables = await fetch('tables.json').then(res => res.json());
})();

function compareAddressComponent(component: string, place: string|string[])
{
	const placeLC = ((Array.isArray(place)? place[0]: place) as string).toLowerCase();
	const compName = component.toLowerCase();
	return [compName, ...compName.split(" ")].includes(placeLC);
}

export function isAddressIn(address: Address, list: (string|string[])[])
{
	if(!address) return false;
	return address.Components.some((component: {name: string;}) =>
		list.some((place: string|string[]) => compareAddressComponent(component.name, place))
	);
}

export function isAddressOneOf(address: Address, list: (string|string[])[])
{
	if(!address) return false;
	return list.some((place: string|string[]) => isAddressAllOf(address, Array.isArray(place)? place: [place]));
}

export interface RouteCostInfo
{
	cost?: number; // стоимость рейса, до скидки, зависящей от стоимости груза
	shoulder?: number; // стоимость плеча для сборных рейсов с платной доставкой
	freeFrom?: number; // стоимость груза, от которой доставка бесплатна (для сборных рейсов)
	route?: string; // undefined для прямого, либо адреса, разделённые знаком ';'
}


export function findRoutes(waypoints: Address[], transportKindNo: string|number, deliveryType: string, manualTripCost: number): RouteCostInfo[]
{
	let res: RouteCostInfo[] = [];
	if(waypoints.length < 2 || transportKindNo === undefined) return res;
	if(!isAddressOneOf(waypoints[0], ["Рязань"])) return res;

	if(waypoints.length === 2)
		for(const [addr, costs] of Object.entries(tables.tripCost))
		{
			const addrComponents = addr.split('<');
			const allMatch = isAddressAllOf(waypoints[1], addrComponents);
			if(!allMatch) continue;
			let newRoute = {cost: costs[transportKindNo]} as RouteCostInfo;
			if(deliveryType === "warehouse")
			{
				if(!isAddressOneOf(waypoints[1], tables.warehousePlaces)) continue;
				res.push(newRoute);
				res.push({freeFrom: 500000, route: addr});
			}
			else res.push(newRoute);
			break;
		}
	if(manualTripCost) res.push({cost: manualTripCost});
	if(waypoints.length === 2 && res.length === 0) res.push({}); // первый элемент зарезервирован для доставки по указанному маршруту, даже если условия неизвестны
	if(deliveryType === "warehouse") return res;

	let hasFreeRoutes = false;
	for(const [addr2, passingTowns] of Object.entries(tables.routesToFreePassingTowns))
	{
		if(waypoints[2] && !isAddressAllOf(waypoints[2], [addr2]))
			continue;
		for(let town of passingTowns)
		{
			let options: {flags?: string, passingFreeFrom?: number} = {};
			if(town[town.length - 1] as any instanceof Object)
			{
				options = town[town.length - 1] as typeof options;
				town = town.slice(0, town.length - 1);
			}
			if(!isAddressOneOf(waypoints[1], [town]))
				continue;
			if(!waypoints[2]) res[0].freeFrom = 7000000;
			const info = {
				freeFrom: options.passingFreeFrom ?? 2000000,
				route: (options.flags || "").includes("r")? addr2 + ";" + town[0]: town[0] + ";" + addr2,
			} as RouteCostInfo;
			const paidRoute = (tables.paidRoutes[addr2] || [])[town[0]];
			if(paidRoute)
			{
				info.cost = paidRoute[transportKindNo];
				info.shoulder = transportKindNo === 0? paidRoute[3]: undefined;
			}
			res.push(info);
			hasFreeRoutes = true;
		}
	}

	if(hasFreeRoutes) return res;

	for(const [addr1, dests] of Object.entries(tables.paidRoutes))
	{
		if(waypoints[2] && !isAddressAllOf(waypoints[1], addr1.split("<")))
			continue;
		for(const [addr2, costs] of Object.entries(dests))
		{
			if(addr2 === "//") continue;
			const isReverse = ((costs[4] || "") as string).includes("r");
			if(!isAddressAllOf(waypoints[2] || waypoints[1], addr2.split("<")))
				continue;
			res.push({
				cost: costs[transportKindNo],
				shoulder: transportKindNo === 0? costs[3]: undefined,
				route: isReverse? addr2 + ";" + addr1: addr1 + ";" + addr2
			});
		}
	}

	return res;
}

export function isRailwayOnly(routeDistanceKm: number, waypoints: Address[])
{
	return routeDistanceKm >= 3000 &&
		!isAddressOneOf(waypoints[1], tables.railwayExceptions);
}

export function warningText(routeDistanceKm: number, waypoints: Address[])
{
	let res = "";
	if(isRailwayOnly(routeDistanceKm, waypoints)) res += "Только ЖД транспортом. Отправка авто только по подтверждению с ООО \"МГК \"Световые Технологии\".\n";
	res += "Поставка опор не осуществляется сборными рейсами и только на платной основе.";
	return res;
}

export function transportKindToNumber(transportKind: string)
{
	return {"truck": 0, "fiveton": 1, "gazelle": 2}[transportKind];
}

export function calcPrice({routeDistanceKm, waypoints, cargoCost, cargoCostAbove4M: cargoCostAbove4M, transportType, deliveryType, route}:
	{
		routeDistanceKm: number|null;
		waypoints: Address[];
		cargoCost: number;
		cargoCostAbove4M: boolean;
		transportType: string;
		deliveryType: string;
		route: RouteCostInfo
	}): number|string|null|undefined
{
	//console.log({routeDistanceKm, fromAddress, toAddress});
	const transportKindNo = transportKindToNumber(transportType);
	const isRailway = isRailwayOnly(routeDistanceKm, waypoints);
	if(route.freeFrom && !isRailway)
	{
		if(cargoCost >= route.freeFrom) return 0;
		//return undefined;
	}

	const isWarehouse = deliveryType === "warehouse";

	if(!cargoCost && isWarehouse && transportKindNo === 0)
		return tables.strings["fill_fields"];

	if(route.shoulder) return route.shoulder; // для рейсов с плечом скидок нет, никакие формулы не применяем
	if(route.cost === undefined)
	{
		if(route.freeFrom) return tables.strings["for_cargo_cost"] + route.freeFrom.toLocaleString('ru') + "₽"
		return undefined;
	}

	if(!isWarehouse && isAddressIn(waypoints[1], tables.fullCostDeliveryPlaces))
		return route.cost;

	if(transportKindNo === 0) // Фура
	{
		if(isWarehouse)
		{
			if(routeDistanceKm === null) return null;
			if(!isRailway)
			{
				if(waypoints.length === 2 && isAddressIn(waypoints[1], ["Москва", "Московская область"]))
					return undefined;
				return Math.round(route.cost * Math.max(0, 1 - cargoCost / 7000000));
			}
			if(!isAddressOneOf(waypoints[1], tables.noFreeDeliveryPlaces) || isAddressOneOf(waypoints[1], tables.halfCostDeliveryPlaces))
				return Math.round(route.cost * Math.max(0.5, 1 - cargoCost / 7000000));
		}
		else return Math.round(route.cost * Math.max(0.5, 1 - cargoCost / 8000000));
	}
	else if(transportKindNo === 1) // Пятитонник
		return Math.round(cargoCostAbove4M? 0.5 * route.cost: 0.7 * route.cost);
	else if(transportKindNo === 2) // Газель
		return Math.round(cargoCostAbove4M? 0.5 * route.cost: route.cost);

	for(const waypoint of waypoints)
		if(!waypoint || !isAddressIn(waypoint, ["Россия"]))
			return null;

	return undefined;
}

export function priceToString(price: number|string)
{
	return typeof price === 'string'? price:
		price === undefined? "по согласованию с СТ.":
		price === null? "невозможна":
		price === 0? "бесплатно":
		isNaN(price)? "ошибка":
		"с доплатой " + price.toLocaleString('ru') + "₽"
}

export function getPopupText({transportType, deliveryType, foundRoutes, routeDistanceKm, waypoints, showInternalInfo}: {
	transportType: string,
	deliveryType: string,
	foundRoutes?: RouteCostInfo[],
	routeDistanceKm: number,
	waypoints: Address[],
	showInternalInfo: boolean
})
{
	const isRailway = isRailwayOnly(routeDistanceKm, waypoints);
	const isWarehouse = deliveryType === "warehouse";
	const isWarehousePlace = isWarehouse && isAddressOneOf(waypoints[1], tables.warehousePlaces);
	let res = "";

	const isFullCostDelivery = !isWarehouse && isAddressIn(waypoints[1], tables.fullCostDeliveryPlaces);
	const isNewRegionDelivery = !isWarehouse && isAddressIn(waypoints[1], tables.newRegions);
	
	if(isFullCostDelivery) {}
	else if(transportType !== 'truck' || !isWarehouse)
	{
		if(!isRailway) res += tables.strings["direct_delivery_cost"] + "\n";
	}
	else
	{
		if(isWarehousePlace)
		{
			const stringifyRouteList = (l: RouteCostInfo[]) => l.map(r => r.route.replace(/;/g, "—").replace(/</g, ",")).join(", ");
			const routeListStr = stringifyRouteList(foundRoutes.filter(r => r.route !== undefined));
			const routeListText = showInternalInfo? " на г. " + routeListStr: "";
			res += tables.strings["free_delivery_from"] + routeListText + ".\n";
		}
	}
	if(isRailway)
	{
		if(res !== "") res += "\n";
		res += tables.strings["railway_compensation"] + "\n";
	}
	else if(transportType === 'truck' && isWarehousePlace)
	{
		if(res !== "") res += "\n";
		res += tables.strings["warehouse_compensation"] + "\n";
	}

	if(isNewRegionDelivery)
	{
		if(res !== "") res += "\n";
		res += tables.strings["new_region_delivery"] + "\n";
	}

	if(isFullCostDelivery)
	{
		if(res !== "") res += "\n";
		res += tables.strings["full_cost_delivery"].replace("{}", tables.fullCostDeliveryPlaces.join(", ")) + "\n";
	}
	else if(transportType === 'truck' && !isWarehouse)
	{
		if(res !== "") res += "\n";
		res += tables.strings["direct_delivery_routes"] + "\n";
	}
	else if(!isRailway)
	{
		if(transportType === 'gazelle')
		{
			if(res !== "") res += "\n";
			res += tables.strings["payment_percent_gazelle"] + "\n";
		}
		else if(transportType === 'fiveton')
		{
			if(res !== "") res += "\n";
			res += tables.strings["payment_percent_fiveton"] + "\n";
		}
	}

	if(!isWarehouse && !isRailway)
	{
		const stringifyRouteList = (l: RouteCostInfo[]) => l.map(r => "Рязань—" + r.route.replace(/;/g, "—").replace(/</g, ",")).join(", ");
		const freeRoutes = foundRoutes.filter(r => r.freeFrom <= 2000000);
		const freeRouteListStr = stringifyRouteList(freeRoutes);
		const paidRouteListStr = stringifyRouteList(foundRoutes.filter(r => (r.cost || r.shoulder) && r.route));

		const paidRouteListText = showInternalInfo? " на " + paidRouteListStr: "";
		if(freeRoutes.length)
		{
			const freeRoutePriceStr = freeRoutes[0].freeFrom / 1000000 + " млн.";
			const freeRouteListText = showInternalInfo? " " + freeRouteListStr: "";
			res += tables.strings["free_delivery"].replace("{}", freeRoutePriceStr).replace("{}", freeRouteListText) + "\n";
		}
		if(paidRouteListStr !== "")
			res += "\n" + tables.strings["paid_delivery_compensation"].replace("{}", paidRouteListText) + "\n";
		if(!freeRoutes.length || paidRouteListStr !== "")
			res += "\n" + tables.strings["planning_note"] + "\n";
	}

	if(isAddressOneOf(waypoints[1], ["Калининград"]))
	{
		if(res !== "") res += "\n";
		res += tables.strings["kaliningrad_delivery"] + "\n";
	}

	if(routeDistanceKm < 3000)
		res += "\n" + tables.strings["tariffs_valid"];

	return res;
}
